import { RootState } from '@/store/store';
import { FiltersGroup } from '@/components/uiKit/FiltersGroup';
import { useAppFilters } from '@/hooks/shared';
import { ASSETS_PREVIEW, ASSETS_PREVIEW_INNER_TABS, FILTER_PARAMS } from '@/types/shared/navigation-tabs.enums';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import {
  setCurrentFiltersValues,
  setCurrentFiltersData,
  getAssetsFiltersData,
  setFiltersByAssetType,
} from '@/store/slices/assetsPreviewSlice';
import { cn } from '@/lib/utils';
import { Asset } from '@/types/models/assets.types';
import { getFilteredAssets } from '@/store/slices/assetsPreviewSlice';
import { FilterOption, FiltersValues } from '@/components/uiKit/FiltersGroup/types';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/shared/display/Icon';
import { CirclePlayIcon, SignalStreamIcon } from '@/assets/Icons';

const renderSensorOption = (option: FilterOption) => {
  const sensorIconStyle = (() => {
    if (option.id?.toString().includes('offline')) return 'text-critical bg-[#CC00001F]';
    else if (option.id?.toString().includes('semi')) return 'text-alarm bg-[#FF891B1F]';
    else if (option.id?.toString().includes('online')) return 'text-healthy bg-[#11975D1F]';
  })();

  return (
    <div
      className={`${option.label?.toString().includes('online') ? 'text-healthy' : 'text-critical'} scale-125 pb-0.5`}
    >
      {/* {item.id?.toString().includes('semi') ? <Icon icon={SignalStreamOfflineIcon} className="text-[#11975D] w-12 h-12" /> : <Icon icon={SignalStreamIcon} className="w-10 h-10" />} */}
      <div
        className={cn(
          'flex items-center justify-center h-6 w-6 bg-primary-400 rounded-full text-white mx-2 ',
          sensorIconStyle
        )}
      >
        <Icon icon={SignalStreamIcon} className="scale-[80%]" />
      </div>
    </div>
  );
};

const sensorsIcon = {
  camera: (
    <div className="flex items-center justify-center h-7 w-7 rounded-full pt-0.5 mx-0.5">
      <Icon icon={CirclePlayIcon} />
    </div>
  ),
  vibrations: <Icon icon={SignalStreamIcon} />,
  current: <Icon icon={SignalStreamIcon} />,
  temperature: <Icon icon={SignalStreamIcon} />,
  speed: <Icon icon={SignalStreamIcon} />,
  'oil-condition': <Icon icon={SignalStreamIcon} />,
  'oil-temperature': <Icon icon={SignalStreamIcon} />,
  'oil-quality': <Icon icon={SignalStreamIcon} />,
  pressure: <Icon icon={SignalStreamIcon} />,
};

const isSensorExists = (option: FilterOption, sensor: string) => {
  return option.label?.toString().toLowerCase().startsWith(sensor);
};

const renderFilterItem = (option: any) => {
  const sensorsTypes = Object.keys(sensorsIcon);
  return (
    <div className={`${option.className || ''} flex items-center justify-between w-full gap-4`}>
      <div className="w-full text-sm">{option.label}</div>
      {option.id?.toString().includes('sensor') && renderSensorOption(option)}
      {sensorsTypes.map((sensor, index) => {
        return (
          isSensorExists(option, sensor) && (
            <div className="scale-150 pb-0.5" key={index}>
              {sensorsIcon[sensor as 'camera']}
            </div>
          )
        );
      })}
    </div>
  );
};

interface AssetPreviewFilterProps {
  assets: Asset[];
}

export const AssetPreviewFilter = ({ assets }: AssetPreviewFilterProps) => {
  const { t } = useTranslation();
  const { assetsType, assetsPreview } = useAppFilters();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const assetsSection = searchParams.get(FILTER_PARAMS.ASSETS_SECTION);
  const { currentFiltersData, initialFiltersValues, allFiltersValues, currentFiltersValues } = useSelector(
    (state: RootState) => state.assetsPreview
  );
  const currentFilters = useSelector((state: RootState) => state.assetsPreview.filtersByAssetType[assetsType]);

  useEffect(() => {
    if (assetsPreview !== 'assets') return;
    if (!currentFilters || !allFiltersValues) return;

    const normalizedFilters = Object.fromEntries(
      Object.keys(allFiltersValues).map((key) => [key, currentFilters[key] || []])
    );
    dispatch(setCurrentFiltersValues(normalizedFilters));

    const filtersData = getAssetsFiltersData(assetsType, assets, normalizedFilters, allFiltersValues);
    dispatch(setCurrentFiltersData(filtersData));
  }, [assetsPreview, dispatch, assetsType, assets, allFiltersValues]);

  const handleFiltersValuesChange = (filtersValues: FiltersValues) => {
    if (!allFiltersValues) return;
    const filtersData = getAssetsFiltersData(assetsType, assets, filtersValues, allFiltersValues);
    dispatch(setCurrentFiltersValues(filtersValues));
    dispatch(setCurrentFiltersData(filtersData));

    if (assetsPreview === 'assets') {
      dispatch(setFiltersByAssetType({ assetType: assetsType, filters: filtersValues }));
    }
  };

  const filteredAssets =
    assets && assetsType && currentFiltersValues ? getFilteredAssets(assets, currentFiltersValues, assetsType) : null;

  const drawerCloseBtnText = filteredAssets
    ? t('assetsPreview.filter.showAssets', { count: filteredAssets.length })
    : '';

  // Don't render for non-assets tabs or when on faults section (FaultsFilter handles that)
  if (assetsPreview !== ASSETS_PREVIEW.ASSETS) return null;
  if (assetsSection === ASSETS_PREVIEW_INNER_TABS.FAULTS) return null;

  return (
    <>
      {currentFiltersData && allFiltersValues && initialFiltersValues && currentFiltersValues && (
        <FiltersGroup
          currentFiltersData={currentFiltersData}
          handleFiltersValuesChange={handleFiltersValuesChange}
          initialFiltersValues={initialFiltersValues}
          currentFiltersValues={currentFiltersValues}
          allFiltersValues={allFiltersValues}
          drawerCloseBtnText={drawerCloseBtnText}
          customItemRender={renderFilterItem}
          hideQuickFiltersResolution={1440}
        />
      )}
    </>
  );
};
